import { SLOT_KEYS, type SlotKey } from '@arena/shared';

/**
 * Data-driven input bindings (DROW-118). Keyboard and mouse share one code space so any action
 * can sit on any key or button: `KeyboardEvent.code` (layout-independent, so WASD is ZQSD on
 * AZERTY) or `Mouse0`..`Mouse4` (`MouseEvent.button`).
 */
export type InputCode = string;
export type MoveDirName = 'up' | 'down' | 'left' | 'right';
export const MOVE_DIRS: readonly MoveDirName[] = ['up', 'down', 'left', 'right'];

export interface Keybinds {
  move: Record<MoveDirName, InputCode>;
  slots: Record<SlotKey, InputCode>;
  /** Spectator only: toggle between following the watched player and a free camera. */
  freeCam: InputCode;
  scoreboard: InputCode;
  /** Toggle sound mute (DROW-120). `''` leaves it unbound. */
  mute: InputCode;
  /**
   * The character's active racial (Stone Guard), cast toward the cursor. On Space since DROW-155
   * (was T). `''` leaves it unbound.
   */
  racial: InputCode;
}

/**
 * Fireball on LMB, Mobility on RMB, the other six slots on 1–6 in slot order, the racial on Space.
 * Free cam sits on C: slot C is on Digit5, so the letter is free.
 */
export const DEFAULT_KEYBINDS: Keybinds = {
  move: { up: 'KeyW', left: 'KeyA', down: 'KeyS', right: 'KeyD' },
  slots: {
    Q: 'Mouse0',
    D: 'Mouse2',
    W: 'Digit1',
    E: 'Digit2',
    R: 'Digit3',
    F: 'Digit4',
    C: 'Digit5',
    V: 'Digit6',
  },
  freeCam: 'KeyC',
  scoreboard: 'Tab',
  mute: 'KeyM',
  racial: 'Space',
};

/** Where free cam goes when the racial takes Space from it: the first of these nothing else uses. */
const FREE_CAM_FALLBACKS: readonly InputCode[] = ['KeyC', 'KeyV', 'KeyG', 'KeyB'];

/**
 * Saved (pre-DROW-155) keybinds → the racial on Space. Runs once, before `normalizeKeybinds`, on
 * the raw blob. Only a racial still on the old default T, or left unbound (almost always the
 * auto-unbind from a clash, not a choice), moves; a racial the player put on another key stays.
 * Space is taken only from free cam or from nothing: if the player bound Space to anything else
 * themselves, the whole set is left as it was. Free cam, when it loses Space, goes to the first
 * free key of C, V, G, B.
 */
export function migrateRacialToSpace(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object') return raw;
  const r = raw as Partial<Record<keyof Keybinds, unknown>>;
  if (r.racial !== undefined && r.racial !== 'KeyT' && r.racial !== '') return raw;
  // Missing entries read as the pre-DROW-155 defaults they stood for (free cam was on Space).
  const values = (saved: unknown, fallback: Record<string, InputCode>): unknown[] => {
    const s = (saved && typeof saved === 'object' ? saved : {}) as Record<string, unknown>;
    return Object.keys(fallback).map((key) => s[key] ?? fallback[key]);
  };
  const freeCam = r.freeCam ?? 'Space';
  const others = new Set<unknown>([
    ...values(r.move, DEFAULT_KEYBINDS.move),
    ...values(r.slots, DEFAULT_KEYBINDS.slots),
    r.scoreboard ?? DEFAULT_KEYBINDS.scoreboard,
    r.mute ?? DEFAULT_KEYBINDS.mute,
  ]);
  if (others.has('Space')) return raw;
  if (freeCam !== 'Space') return { ...r, racial: 'Space' };
  const moved = FREE_CAM_FALLBACKS.find((code) => !others.has(code));
  return moved ? { ...r, racial: 'Space', freeCam: moved } : raw;
}

/**
 * Inputs with a fixed role that no binding may take: Esc cancels an armed spell. (LMB confirms and
 * RMB cancels while a spell is armed, but they stay bindable; that role only wins while armed.)
 */
export const RESERVED_CODES: ReadonlySet<InputCode> = new Set(['Escape']);

export type BoundAction =
  | { kind: 'move'; dir: MoveDirName }
  | { kind: 'cast'; slot: SlotKey }
  | { kind: 'freeCam' }
  | { kind: 'scoreboard' }
  | { kind: 'mute' }
  | { kind: 'racial' };

const MOUSE_CODE = /^Mouse([0-4])$/;
/** Plausible `KeyboardEvent.code` values (`KeyA`, `Digit1`, `ArrowUp`, `F5`, …) or a mouse button. */
const VALID_CODE = /^(?:Mouse[0-4]|[A-Z][A-Za-z0-9]{1,24})$/;

function isBindable(code: unknown): code is InputCode {
  return typeof code === 'string' && VALID_CODE.test(code) && !RESERVED_CODES.has(code);
}

/** Every (code, action) pair, in a fixed order. */
function entries(k: Keybinds): [InputCode, BoundAction][] {
  return [
    ...MOVE_DIRS.map((dir): [InputCode, BoundAction] => [k.move[dir], { kind: 'move', dir }]),
    ...SLOT_KEYS.map((slot): [InputCode, BoundAction] => [k.slots[slot], { kind: 'cast', slot }]),
    [k.freeCam, { kind: 'freeCam' }],
    [k.scoreboard, { kind: 'scoreboard' }],
    ...(k.mute ? [[k.mute, { kind: 'mute' }] as [InputCode, BoundAction]] : []),
    ...(k.racial ? [[k.racial, { kind: 'racial' }] as [InputCode, BoundAction]] : []),
  ];
}

/** One lookup for keyboard and mouse, so the controller never hard-codes a slot or a button. */
export function indexBindings(k: Keybinds): Map<InputCode, BoundAction> {
  return new Map(entries(k));
}

/**
 * Saved keybinds → a usable set. Missing or invalid entries take their default; if any code then
 * ends up bound to two actions (e.g. a slot on W while W still moves), the whole set resets to
 * DEFAULT_KEYBINDS rather than guessing which of the two the player meant.
 */
export function normalizeKeybinds(raw: unknown): Keybinds {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Partial<Record<keyof Keybinds, unknown>>;
  const group = <K extends string>(
    saved: unknown,
    keys: readonly K[],
    fallback: Record<K, InputCode>,
  ): Record<K, InputCode> => {
    const s = (saved && typeof saved === 'object' ? saved : {}) as Record<string, unknown>;
    return Object.fromEntries(
      keys.map((key) => [key, isBindable(s[key]) ? s[key] : fallback[key]]),
    ) as Record<K, InputCode>;
  };
  const d = DEFAULT_KEYBINDS;
  const k: Keybinds = {
    move: group(r.move, MOVE_DIRS, d.move),
    slots: group(r.slots, SLOT_KEYS, d.slots),
    freeCam: isBindable(r.freeCam) ? r.freeCam : d.freeCam,
    scoreboard: isBindable(r.scoreboard) ? r.scoreboard : d.scoreboard,
    mute: isBindable(r.mute) || r.mute === '' ? r.mute : d.mute,
    racial: isBindable(r.racial) || r.racial === '' ? r.racial : d.racial,
  };
  // Mute and racial came later than the rest: if their key is already taken, leave them unbound
  // rather than resetting bindings the player chose. Newest first, so a saved mute on T wins.
  if (k.racial && entries({ ...k, racial: '' }).some(([code]) => code === k.racial)) k.racial = '';
  if (k.mute && entries({ ...k, mute: '' }).some(([code]) => code === k.mute)) k.mute = '';
  const all = entries(k);
  return new Set(all.map(([code]) => code)).size === all.length ? k : DEFAULT_KEYBINDS;
}

const MOUSE_LABELS = ['LMB', 'MMB', 'RMB', 'M4', 'M5'];

/**
 * Short label for an input: 'Mouse0' → 'LMB', 'Mouse2' → 'RMB', 'Digit1' → '1', 'KeyQ' → 'Q'.
 * `compact` is for the small HUD key badge, where 'Space' reads 'SPC'.
 */
export function inputLabel(code: InputCode, compact = false): string {
  const mouse = MOUSE_CODE.exec(code);
  if (mouse) return MOUSE_LABELS[Number(mouse[1])] ?? code;
  if (compact && code === 'Space') return 'SPC';
  return code.replace(/^(?:Key|Digit)(?=.)/, '');
}

export function isMouseCode(code: InputCode): boolean {
  return MOUSE_CODE.test(code);
}

/**
 * Slots in display order: mouse-bound slots first (by button), then key-bound ones by label
 * (digits numerically). Drives the spell bar and shop, so rebinding reorders them with no code
 * change.
 */
export function slotsInBindingOrder(k: Keybinds): SlotKey[] {
  const rank = (slot: SlotKey): [number, string] => {
    const code = k.slots[slot];
    const mouse = MOUSE_CODE.exec(code);
    return mouse ? [Number(mouse[1]), ''] : [MOUSE_LABELS.length, inputLabel(code)];
  };
  return [...SLOT_KEYS].sort((a, b) => {
    const [ga, la] = rank(a);
    const [gb, lb] = rank(b);
    return (
      ga - gb ||
      la.localeCompare(lb, 'en', { numeric: true }) ||
      SLOT_KEYS.indexOf(a) - SLOT_KEYS.indexOf(b)
    );
  });
}

/** Every bindable action, in the order the Controls tab lists them. */
export const BINDABLE_ACTIONS: readonly BoundAction[] = [
  ...MOVE_DIRS.map((dir): BoundAction => ({ kind: 'move', dir })),
  ...SLOT_KEYS.map((slot): BoundAction => ({ kind: 'cast', slot })),
  { kind: 'racial' },
  { kind: 'scoreboard' },
  { kind: 'mute' },
  { kind: 'freeCam' },
];

/** Stable id for an action ('move.up', 'cast.Q', 'mute'), for React keys and lookups. */
export function actionId(a: BoundAction): string {
  return a.kind === 'move' ? `move.${a.dir}` : a.kind === 'cast' ? `cast.${a.slot}` : a.kind;
}

/** Mute and the racial may sit unbound (`''`); every other action always has a key. */
export function isOptionalAction(a: BoundAction): boolean {
  return a.kind === 'mute' || a.kind === 'racial';
}

/** The input an action is on (`''` for an unbound mute or racial). */
export function boundCode(k: Keybinds, a: BoundAction): InputCode {
  switch (a.kind) {
    case 'move':
      return k.move[a.dir];
    case 'cast':
      return k.slots[a.slot];
    default:
      return k[a.kind];
  }
}

function withCode(k: Keybinds, a: BoundAction, code: InputCode): Keybinds {
  switch (a.kind) {
    case 'move':
      return { ...k, move: { ...k.move, [a.dir]: code } };
    case 'cast':
      return { ...k, slots: { ...k.slots, [a.slot]: code } };
    default:
      return { ...k, [a.kind]: code };
  }
}

/**
 * Puts `action` on `code` (the Controls tab editor, DROW-173). If another action already has that
 * input it never ends up on two: a required action swaps onto the edited action's old input; mute
 * or the racial goes unbound. The one move that can't work, an unbound mute/racial taking a
 * required action's input (nothing to swap it onto), and an unbindable code (Esc, junk) return
 * `k` unchanged. From a normalized set the result is normalized too, so `normalizeKeybinds` on
 * reload keeps it exactly instead of resetting to the defaults.
 */
export function rebind(k: Keybinds, action: BoundAction, code: InputCode): Keybinds {
  const old = boundCode(k, action);
  if (!isBindable(code) || code === old) return k;
  const id = actionId(action);
  const other = BINDABLE_ACTIONS.find((a) => actionId(a) !== id && boundCode(k, a) === code);
  const next = withCode(k, action, code);
  if (!other) return next;
  if (isOptionalAction(other)) return withCode(next, other, '');
  return old ? withCode(next, other, old) : k;
}

/**
 * Keys the browser or OS keeps for itself, so a binding on them would never fire: reload (F5),
 * fullscreen (F11), dev tools (F12), and the Meta/OS key. Anything pressed with Ctrl, Alt or Meta
 * held (Ctrl+W, Alt+F4, …) is a chord, and bindings have no modifiers, so those are refused too.
 */
const BROWSER_CODES: ReadonlySet<InputCode> = new Set([
  'F5',
  'F11',
  'F12',
  'MetaLeft',
  'MetaRight',
  'OSLeft',
  'OSRight',
  'ContextMenu',
]);
const MODIFIER_CODE = /^(?:Control|Alt|Shift|Meta|OS)(?:Left|Right)$/;

/**
 * What a key press in the binding editor means: an input to bind, or why it can't be bound. Esc
 * is not handled here; it cancels the capture through the Esc layer stack.
 */
export function captureKey(e: {
  code: string;
  ctrlKey: boolean;
  altKey: boolean;
  metaKey: boolean;
}): { code: InputCode } | { rejected: string } {
  const { code } = e;
  // A modifier pressed on its own reports itself as held; that is a key, not a chord.
  const chord = !MODIFIER_CODE.test(code) && (e.ctrlKey || e.altKey || e.metaKey);
  if (chord || BROWSER_CODES.has(code)) return { rejected: 'That key belongs to the browser.' };
  // Some keys (media, IME) report no physical code.
  if (!isBindable(code) || code === 'Unidentified') return { rejected: 'That key can’t be bound.' };
  return { code };
}
